import { Chessboard } from 'cm-chessboard';
import { FelixChessNavigation } from './navigation.js';
import { FelixChessBoardCaptions } from './board-captions.js';
import { FelixChessReplayController } from './replay-controller.js';
import { FelixChessMoveList } from './movelist.js';
import { FelixChessAnalyzer } from './analyzer.js';
import { FelixChessEvalBar } from './evalbar.js';
import { FelixChessVariationController } from './variation-controller.js';
import { FelixChessVariationInputController } from './variation-input-controller.js';
import { buildAnalysisFilename, buildPgnWithVariations, downloadTextFile } from './pgn-export.js';
import { Markers, MARKER_TYPE } from 'cm-chessboard/src/extensions/markers/Markers.js';
import { Arrows } from 'cm-chessboard/src/extensions/arrows/Arrows.js';
import { BoardMoveInput } from './board-move-input.js';
import { isPositionRendering } from './position-render-state.js';
import { positionContext, validatePositionFen, scopedPositionIdentity } from './position-identity.js';

(function () {
    console.log('FelixChess Board Controller loaded');

    const START_FEN = 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';
    const ASSETS_URL = '/etc/vendor-libraries/felixchess-versioned-library/versions/0.0.1/assets/';
    const SOUND_URL = '/apps/felixchess/resources/sounds/';

    window.FelixChess = window.FelixChess || {};

    function createBoardSoundController() {
        const STORAGE_KEY = 'felixchess.sound.enabled';
        const listeners = new Set();

        function readStoredEnabled() {
            try {
                return window.localStorage?.getItem(STORAGE_KEY) !== 'false';
            } catch (_) {
                return true;
            }
        }

        let soundEnabled = readStoredEnabled();
        const canUseAudio = typeof window !== 'undefined' && typeof window.Audio === 'function';
        const sounds = canUseAudio
            ? {
                move: new Audio(`${SOUND_URL}move.mp3`),
                capture: new Audio(`${SOUND_URL}capture.mp3`)
            }
            : {};

        Object.values(sounds).forEach((audio) => {
            audio.preload = 'auto';
        });

        function playSound(audio) {
            if (!soundEnabled || !audio) return;
            try {
                audio.currentTime = 0;
                const promise = audio.play();
                if (promise && typeof promise.catch === 'function') {
                    promise.catch(() => {});
                }
            } catch (_) {
                // Browser autoplay policies or unsupported codecs must never
                // break board navigation.
            }
        }

        function notify() {
            listeners.forEach((listener) => {
                try {
                    listener(soundEnabled);
                } catch (_) {
                    // A broken UI listener must not break board navigation.
                }
            });
        }

        return {
            isEnabled() {
                return soundEnabled;
            },
            setEnabled(enabled) {
                soundEnabled = enabled !== false;
                try {
                    window.localStorage?.setItem(STORAGE_KEY, soundEnabled ? 'true' : 'false');
                } catch (_) {
                    // Storage may be unavailable in private or embedded contexts.
                }
                notify();
            },
            toggle() {
                this.setEnabled(!soundEnabled);
                return soundEnabled;
            },
            onChange(listener) {
                if (typeof listener !== 'function') {
                    return () => {};
                }
                listeners.add(listener);
                listener(soundEnabled);
                return () => listeners.delete(listener);
            },
            playMove() {
                playSound(sounds.move);
            },
            playCapture() {
                playSound(sounds.capture);
            },
            playFromMove(move) {
                if (!move) return;
                if (move.captured) {
                    this.playCapture();
                } else {
                    this.playMove();
                }
            }
        };
    }

    const boardSounds = window.FelixChess.BoardSounds || createBoardSoundController();
    window.FelixChess.BoardSounds = boardSounds;

    // ─── PGN clock parser ─────────────────────────────────────────────────────
    /**
     * Extract per-move clock values from a full PGN string.
     *
     * Returns a clocks[] array parallel to the moves[] array:
     *   clocks[0] = remaining time string after ply 1 (White's first move)
     *   clocks[1] = remaining time string after ply 2 (Black's first move)
     *   …
     * Each entry is either a "H:MM:SS" string or null (no annotation for that ply).
     *
     * Clock annotations are extracted from brace comments:
     *   e4 {[%clk 0:25:18]}  →  '0:25:18'
     *
     * Usage (in opening-explorer.js or wherever you parse the PGN):
     *   const { moves } = yourExistingParser(pgn);
     *   const clocks    = FelixChess.parsePgnClocks(pgn);
     *   FelixChess.BoardController.loadGame(rootEl, { moves, clocks, … });
     *
     * @param {string} pgn - the full PGN string from pgn:pgn
     * @returns {Array<string|null>}
     */
    function parsePgnClocks(pgn) {
        if (!pgn || typeof pgn !== 'string') return [];

        // Strip PGN header tags  [Tag "Value"]
        let body = pgn.replace(/\[[^\]]*\]\s*/g, '').trim();

        // Remove parenthetical variations iteratively (nested up to ~4 deep)
        let prev;
        do {
            prev = body;
            body = body.replace(/\([^()]*\)/g, '');
        } while (body !== prev);

        // Strip result marker at end
        body = body.replace(/(?:1-0|0-1|1\/2-1\/2|\*)\s*$/, '').trim();

        const clocks = [];

        // Tokenise: (1) move numbers,  (2) brace comments,  (3) SAN move tokens
        // SAN covers: castling, piece moves (with optional file/rank disambiguation,
        // capture, promotion, check/mate suffix), pawn moves and promotions.
        const TOKEN_RE = /\d+\.+|\{([^}]*)\}|([KQRBN][a-h1-8]?x?[a-h][1-8](?:=[QRBN])?[+#!?]*|[a-h](?:x[a-h])?[1-8](?:=[QRBN])?[+#!?]*|O-O(?:-O)?[+#!?]*)/g;

        let m;
        let expectComment = false;  // true right after pushing a move token

        while ((m = TOKEN_RE.exec(body)) !== null) {
            if (/^\d+\.+/.test(m[0])) {
                // Move number — ignore
                continue;
            }

            if (m[1] !== undefined) {
                // Brace comment { … }
                if (expectComment && clocks.length > 0) {
                    const clkMatch = /\[%clk\s+(\d+:\d{2}:\d{2})\]/.exec(m[1]);
                    if (clkMatch) {
                        clocks[clocks.length - 1] = clkMatch[1];
                    }
                }
                expectComment = false;
            } else if (m[2]) {
                // SAN move token
                clocks.push(null);      // placeholder; updated by following comment
                expectComment = true;
            }
        }

        return clocks;
    }

    function createBoardInstance(rootEl) {
        if (!rootEl) {
            return null;
        }

        if (rootEl.__fcBoardInstance) {
            return rootEl.__fcBoardInstance;
        }

        // Consumers such as Game Analyzer keep the boardviewer root hidden
        // until a game is selected. Reveal this root before measuring; if an
        // ancestor is still hidden, isVisible() below will still return false.
        rootEl.hidden = false;

        if (!isVisible(rootEl)) {
            return null;
        }

        const boardEl = rootEl.querySelector('[data-fc-board]');
        if (!boardEl) {
            console.warn('FelixChessBoardController: missing [data-fc-board] element', rootEl);
            return null;
        }

        const board = new Chessboard(boardEl, {
            position: START_FEN,
            assetsUrl: ASSETS_URL,
            style: {
                animationDuration: 300
            },
            extensions: [{ class: Markers }, { class: Arrows }]
        });

        rootEl.__fcBoardInstance = board;
        return board;
    }

    function getCaptions(rootEl) {
        if (!rootEl) {
            return null;
        }

        if (rootEl.__fcCaptionsInstance) {
            return rootEl.__fcCaptionsInstance;
        }

        const topMetaEl = rootEl.querySelector('[data-fc-top-meta]');
        const navEl = rootEl.querySelector('[data-fc-nav]');

        if (!topMetaEl && !navEl) {
            console.warn('FelixChessBoardController: missing caption target elements', rootEl);
            return null;
        }

        const captions = new FelixChessBoardCaptions({
            rootEl,
            topMetaEl,
            navEl,
            context: rootEl.dataset.boardContext || 'default'
        });

        rootEl.__fcCaptionsInstance = captions;

        // Wire replay if it already exists so clock updates start immediately
        const existingReplay = rootEl.__fcReplayInstance;
        if (existingReplay) {
            captions.setReplay(existingReplay);
        }

        return captions;
    }

    function getNavigator(rootEl) {
        if (!rootEl) {
            return null;
        }

        if (rootEl.__fcNavigatorInstance) {
            return rootEl.__fcNavigatorInstance;
        }

        const navEl = rootEl.querySelector('[data-fc-nav]');
        if (!navEl) {
            console.warn('FelixChessBoardController: missing [data-fc-nav] element', rootEl);
            return null;
        }

        const replay = rootEl.__fcReplayInstance || getReplay(rootEl);

        const navigator = new FelixChessNavigation({
            rootEl,
            navEl,
            replay,
            context: rootEl.dataset.boardContext || 'default',
            soundController: boardSounds
        });

        rootEl.__fcNavigatorInstance = navigator;
        return navigator;
    }

    function getReplay(rootEl) {
        if (!rootEl) {
            return null;
        }

        if (rootEl.__fcReplayInstance) {
            return rootEl.__fcReplayInstance;
        }

        const board = rootEl.__fcBoardInstance || createBoardInstance(rootEl);
        if (!board) {
            return null;
        }

        const replay = new FelixChessReplayController({
            board,
            rootEl,
            context: rootEl.dataset.boardContext || 'default',
            soundController: boardSounds
        });

        rootEl.__fcReplayInstance = replay;

        // Wire captions if they already exist (creation order can vary)
        const existingCaptions = rootEl.__fcCaptionsInstance;
        if (existingCaptions) {
            existingCaptions.setReplay(replay);
        }

        return replay;
    }

    function syncFeatureFlags(rootEl) {
        if (!rootEl) {
            return {};
        }

        rootEl.__fcFeatureFlags = {
            ...(rootEl.__fcFeatureFlags || {}),
            variationExploration: rootEl.dataset.enableVariationExploration === 'true',
            pgnExport: rootEl.dataset.enablePgnExport === 'true'
        };
        return rootEl.__fcFeatureFlags;
    }

    function getVariationController(rootEl) {
        if (!rootEl) {
            return null;
        }

        const flags = syncFeatureFlags(rootEl);
        if (!flags.variationExploration) {
            if (rootEl.__fcVariationController) {
                rootEl.__fcVariationController.destroy();
                rootEl.__fcVariationController = null;
            }
            return null;
        }

        if (rootEl.__fcVariationController) {
            return rootEl.__fcVariationController;
        }

        const replay = rootEl.__fcReplayInstance || getReplay(rootEl);
        if (!replay) {
            return null;
        }

        const controller = new FelixChessVariationController({
            rootEl,
            replay
        });
        rootEl.__fcVariationController = controller;
        return controller;
    }

    function getVariationInputController(rootEl) {
        if (!rootEl) {
            return null;
        }

        const flags = syncFeatureFlags(rootEl);
        if (!flags.variationExploration) {
            if (rootEl.__fcVariationInputController) {
                rootEl.__fcVariationInputController.destroy();
                rootEl.__fcVariationInputController = null;
            }
            return null;
        }

        if (rootEl.__fcVariationInputController) {
            return rootEl.__fcVariationInputController;
        }

        const board = rootEl.__fcBoardInstance || createBoardInstance(rootEl);
        const replay = rootEl.__fcReplayInstance || getReplay(rootEl);
        const variationController = getVariationController(rootEl);
        if (!board || !replay || !variationController) {
            return null;
        }

        const inputController = new FelixChessVariationInputController({
            rootEl,
            board,
            replay,
            variationController,
            soundController: boardSounds
        });
        rootEl.__fcVariationInputController = inputController;
        return inputController;
    }

    function getMoveList(rootEl) {
        if (!rootEl) {
            return null;
        }

        if (rootEl.__fcMoveListInstance) {
            if (typeof rootEl.__fcMoveListInstance.setVariationController === 'function') {
                rootEl.__fcMoveListInstance.setVariationController(getVariationController(rootEl));
            }
            return rootEl.__fcMoveListInstance;
        }

        const listEl = rootEl.querySelector('[data-fc-movelist]');
        if (!listEl) {
            console.warn('FelixChessBoardController: missing [data-fc-movelist] element', rootEl);
            return null;
        }

        const replay = rootEl.__fcReplayInstance || getReplay(rootEl);
        if (!replay) {
            console.warn('FelixChessBoardController: replay controller missing for movelist', rootEl);
            return null;
        }

        const moveList = new FelixChessMoveList({
            rootEl,
            listEl,
            replay,
            context: rootEl.dataset.boardContext || 'default',
            layout: rootEl.dataset.movelistLayout || 'horizontal'
        });
        moveList.setVariationController(getVariationController(rootEl));

        rootEl.__fcMoveListInstance = moveList;
        return moveList;
    }

    function getAnalyzer(rootEl) {
        if (!rootEl) {
            return null;
        }

        if (rootEl.__fcAnalyzerInstance) {
            return rootEl.__fcAnalyzerInstance;
        }

        const analyzerEl = rootEl.querySelector('[data-fc-analyzer]');
        if (!analyzerEl) {
            console.warn('FelixChessBoardController: missing [data-fc-analyzer] element', rootEl);
            return null;
        }

        const replay = rootEl.__fcReplayInstance || getReplay(rootEl);
        if (!replay) {
            console.warn('FelixChessBoardController: replay controller missing for analyzer', rootEl);
            return null;
        }

        const parsedDepth = parseInt(rootEl.dataset.analysisDepth, 10);
        const parsedMultiPv = parseInt(rootEl.dataset.analysisMultipv, 10);
        const parsedWaitMs = parseInt(rootEl.dataset.analysisWait, 10);
        const parsedUpdateMs = parseInt(rootEl.dataset.analysisUpdate, 10);

        const analyzer = new FelixChessAnalyzer({
            rootEl,
            analyzerEl,
            replay,
            context: rootEl.dataset.boardContext || 'default',
            engineName: rootEl.dataset.engineName || 'Stockfish 18 Lite',
            engineUrl: rootEl.dataset.engineUrl
                || `${ASSETS_URL}stockfish/stockfish-18-lite-single.js`,
            depth: Number.isFinite(parsedDepth) ? parsedDepth : 21,
            multiPv: Number.isFinite(parsedMultiPv) ? parsedMultiPv : 3,
            waitMs: Number.isFinite(parsedWaitMs) ? parsedWaitMs : 300,
            updateMs: Number.isFinite(parsedUpdateMs) ? parsedUpdateMs : 250,
            enableNullMoveAnalysis: rootEl.dataset.enableNullMoveAnalysis === 'true',
            enablePgnExport: rootEl.dataset.enablePgnExport === 'true',
            onExportPgn: () => exportPgn(rootEl)
        });

        rootEl.__fcAnalyzerInstance = analyzer;
        return analyzer;
    }

    function getEvalBar(rootEl) {
        if (!rootEl) {
            return null;
        }

        if (rootEl.__fcEvalBarInstance) {
            return rootEl.__fcEvalBarInstance;
        }

        const evalBarEl = rootEl.querySelector('[data-fc-evalbar]');
        if (!evalBarEl) {
            console.warn('FelixChessBoardController: missing [data-fc-evalbar] element', rootEl);
            return null;
        }

        const evalBar = new FelixChessEvalBar({
            rootEl,
            evalBarEl,
            orientation: rootEl.dataset.evalbarOrientation || 'vertical',
            showScore: rootEl.dataset.evalbarScore !== 'false',
            scorePerspective: rootEl.dataset.evalbarPerspective || 'white'
        });

        rootEl.__fcEvalBarInstance = evalBar;
        return evalBar;
    }

    function initViewer(rootEl) {
        if (!rootEl) {
            return;
        }

        if (rootEl.dataset.fcViewerInitialized === 'true') {
            return;
        }

        const board = createBoardInstance(rootEl);
        if (!board) {
            return;
        }

        getReplay(rootEl);
        getVariationController(rootEl);
        getVariationInputController(rootEl);

        if (areCaptionsEnabled(rootEl)) {
            getCaptions(rootEl);
        }

        if (isNavigationEnabled(rootEl)) {
            getNavigator(rootEl);
        }

        if (isMoveListEnabled(rootEl)) {
            getMoveList(rootEl);
        }

        if (isAnalyzerEnabled(rootEl)) {
            getAnalyzer(rootEl);
        }

        if (isEvalBarEnabled(rootEl)) {
            getEvalBar(rootEl);
        }

        rootEl.dataset.fcViewerInitialized = 'true';
        console.log('FelixChessBoardController: viewer initialized', rootEl);
    }

    function initAll() {
        const viewers = document.querySelectorAll('[data-fc-viewer]');
        viewers.forEach(initViewer);
    }

    function setFen(rootEl, fen) {
        if (!rootEl || !fen) {
            return;
        }

        const board = rootEl.__fcBoardInstance || createBoardInstance(rootEl);
        if (!board) {
            return;
        }

        // Route through the replay controller so the 'gameloaded' / 'positionchange'
        // events fire. The analyzer and eval bar both subscribe to those events —
        // calling board.setPosition() directly bypasses the event chain entirely,
        // so Stockfish never starts and the eval bar stays hidden.
        //
        // loadGame({ startFen: fen, moves: [] }) is safe: it calls
        // replay.clear(false) internally before loading, so no double-clear
        // event is emitted. The opening explorer is unaffected because it
        // calls loadGame() directly and never goes through setFen().
        const replay = getReplay(rootEl);
        if (replay) {
            replay.loadGame({ startFen: fen, moves: [] });
        } else {
            // Fallback: replay not wired up yet (should not normally happen)
            clearGame(rootEl);
            board.setPosition(fen, true);
        }
    }

    function loadGame(rootEl, options = {}) {
        if (!rootEl) {
            return;
        }

        const board = rootEl.__fcBoardInstance || createBoardInstance(rootEl);
        if (!board) {
            return;
        }

        const captions = areCaptionsEnabled(rootEl)
            ? getCaptions(rootEl)
            : null;

        if (isNavigationEnabled(rootEl)) {
            getNavigator(rootEl);
        }

        if (isMoveListEnabled(rootEl)) {
            getMoveList(rootEl);
        }

        if (isAnalyzerEnabled(rootEl)) {
            getAnalyzer(rootEl);
        }

        if (isEvalBarEnabled(rootEl)) {
            getEvalBar(rootEl);
        }

        const startFen = options.startFen || START_FEN;
        const moves = Array.isArray(options.moves) ? options.moves : [];
        const positions = Array.isArray(options.positions) ? options.positions : [];
        const clocks = Array.isArray(options.clocks) ? options.clocks : [];
        const initialClocks = options.initialClocks || options.meta?.initialClocks || null;
        console.log('[FelixChess][board-controller] loadGame clocks', clocks, initialClocks);
        console.debug('[FelixChess DEBUG BUILD] BoardController fenList source fix', {
            replayMode: options.replayMode || '',
            positionsLength: positions.length,
            firstFen: positions[0]?.fen || positions[0] || '',
            initialFen: options.initialFen || '',
            startFen,
            variant: options.variant || options.meta?.variant || '',
            gameType: options.gameType || options.meta?.gameType || '',
            spid: options.spid ?? options.meta?.spid ?? '',
            backrank: options.backrank || options.backRank || options.meta?.backrank || ''
        });
        const initialPly = Number.isInteger(options.initialPly)
            ? options.initialPly
            : parseInt(options.initialPly, 10) || 0;

        const white = options.white || null;
        const black = options.black || null;
        const meta = options.meta || null;

        const replay = getReplay(rootEl);
        if (!replay) {
            return;
        }
        rootEl.__fcPositionContext = {
            variant: options.variant || meta?.variant || '',
            gameType: options.gameType || meta?.gameType || '',
            spid: options.spid ?? meta?.spid ?? '',
            backrank: options.backrank || options.backRank || meta?.backrank || meta?.backRank || ''
        };
        getVariationController(rootEl);
        getVariationInputController(rootEl);

        if (captions) {
            captions.loadGameMeta({
                white,
                black,
                meta
            });
        }

        const replayOptions = {
            startFen,
            initialFen: options.initialFen || '',
            moves,
            positions,
            replayMode: options.replayMode || '',
            clocks,
            initialClocks,
            initialPly,
            variant: options.variant || options.meta?.variant || '',
            gameType: options.gameType || options.meta?.gameType || '',
            spid: options.spid ?? options.meta?.spid ?? '',
            backRank: options.backRank || options.meta?.backRank || '',
            backrank: options.backrank || options.backRank || options.meta?.backrank || '',
            white,
            black,
            meta
        };
        console.debug('[FelixChess][board-controller] replayOptions', {
            replayMode: replayOptions.replayMode,
            positionsLength: replayOptions.positions.length,
            firstFen: replayOptions.positions[0]?.fen || replayOptions.positions[0] || ''
        });
        replay.loadGame(replayOptions);
    }

    function loadTreePath(rootEl, options = {}) {
        if (!rootEl) return;

        const board = rootEl.__fcBoardInstance || createBoardInstance(rootEl);
        if (!board) return;

        // Ensure all sub-controllers exist
        if (isAnalyzerEnabled(rootEl))  getAnalyzer(rootEl);
        if (isEvalBarEnabled(rootEl))   getEvalBar(rootEl);
        if (isNavigationEnabled(rootEl)) getNavigator(rootEl);
        if (isMoveListEnabled(rootEl))   getMoveList(rootEl);

        const startFen  = options.startFen || START_FEN;
        const moves     = Array.isArray(options.moves) ? options.moves : [];
        const children  = Array.isArray(options.children) ? options.children : [];
        const onVarClick = typeof options.onVariationClick === 'function'
            ? options.onVariationClick : null;

        // Load the ancestor path so replay + analyzer fire correctly.
        // initialPly = moves.length positions the board at the selected node.
        const replay = getReplay(rootEl);
        if (!replay) return;
        getVariationInputController(rootEl);

        replay.loadGame({
            startFen,
            moves,
            initialPly: moves.length
        });

        // Push variation children to the move list
        const moveList = rootEl.__fcMoveListInstance;
        if (moveList) {
            moveList.setVariations(children, onVarClick);
        }
    }

    function clearGame(rootEl) {
        if (!rootEl) {
            return;
        }
        rootEl.__fcPositionContext = null;

        const captions = rootEl.__fcCaptionsInstance;
        if (captions) {
            captions.clear();
        }

        const navigator = rootEl.__fcNavigatorInstance;
        if (navigator) {
            navigator.clear();
        }

        const moveList = rootEl.__fcMoveListInstance;
        if (moveList) {
            moveList.clear();
        }

        const analyzer = rootEl.__fcAnalyzerInstance;
        if (analyzer) {
            analyzer.clear();
        }

        const replay = rootEl.__fcReplayInstance;
        if (replay) {
            replay.clear();
        }

        const variationInputController = rootEl.__fcVariationInputController;
        if (variationInputController) {
            variationInputController.clear();
        }

        const variationController = rootEl.__fcVariationController;
        if (variationController) {
            variationController.clear();
        }
    }

    /** Snapshot the displayed placement with its active full-FEN owner, never a PGN index alone. */
    function getCurrentPosition(rootEl) {
        try {
            const board = rootEl?.__fcBoardInstance;
            const replayState = rootEl?.__fcReplayInstance?.getState();
            if (!board || !replayState?.hasGame || isPositionRendering(board)) return null;
            const exploration = rootEl.__fcVariationInputController;
            if (exploration?.pendingPosition) return null;
            const fen = exploration?.isActive()
                ? exploration.getCurrentFen() : replayState.currentFen;
            if (!fen || board.getPosition().split(/\s+/)[0] !== fen.split(/\s+/)[0]) return null;
            const context = { ...replayState.meta, ...rootEl.__fcPositionContext };
            const namespace = positionContext(context);
            if (namespace.supported && !validatePositionFen(fen, context).ok) return null;
            return { fen, origin: exploration?.isActive() ? 'variation' : 'mainline',
                ply: replayState.currentIndex, side: fen.split(/\s+/)[1], variant: namespace.variant,
                gamePath: context.path || '', gameType: context.gameType || '', spid: namespace.spid, backrank: context.backrank || '',
                searchable: namespace.supported };
        } catch (_) {
            return null;
        }
    }

    /** Seek the first indexed-equivalent occurrence in the loaded mainline. */
    function seekPosition(rootEl, fen, context = {}) {
        const replay = rootEl?.__fcReplayInstance;
        const key = scopedPositionIdentity(fen, context);
        if (!key || !replay) return false;
        const replayState = replay.getState();
        const loadedContext = { ...replayState.meta, ...rootEl.__fcPositionContext };
        const ply = replayState.positions.findIndex(position => scopedPositionIdentity(position, loadedContext) === key);
        if (ply < 0) return false;
        replay.goTo(ply, false, 'position-search');
        return true;
    }

    function exportPgn(rootEl) {
        if (!rootEl) {
            return false;
        }

        const flags = syncFeatureFlags(rootEl);
        if (!flags.pgnExport) {
            return false;
        }

        const replay = rootEl.__fcReplayInstance || getReplay(rootEl);
        if (!replay || typeof replay.getState !== 'function') {
            return false;
        }

        const replayState = replay.getState();
        if (!replayState || !replayState.hasGame) {
            return false;
        }

        const variations = rootEl.__fcVariationController
            && typeof rootEl.__fcVariationController.getAllVariations === 'function'
            ? rootEl.__fcVariationController.getAllVariations()
            : [];
        const pgn = buildPgnWithVariations({ replayState, variations });
        const filename = buildAnalysisFilename(replayState);
        downloadTextFile(filename, pgn);
        return true;
    }

    function reset(rootEl) {
        if (!rootEl) {
            return;
        }

        const board = rootEl.__fcBoardInstance || createBoardInstance(rootEl);
        if (!board) {
            return;
        }

        clearGame(rootEl);
        board.setPosition(START_FEN, false);
    }

    function isVisible(el) {
        if (!el) return false;
        // offsetParent is null for elements inside position:fixed containers,
        // even when the element is fully visible. Fall back to clientWidth check
        // when the computed position chain includes a fixed ancestor.
        if (el.clientWidth > 0) return true;
        // Element has no width — genuinely not visible.
        return false;
    }

    function isNavigationEnabled(rootEl) {
        return rootEl.dataset.navigation !== 'false';
    }

    function areCaptionsEnabled(rootEl) {
        return rootEl.dataset.captions !== 'false';
    }

    function isMoveListEnabled(rootEl) {
        return rootEl.dataset.movelist !== 'false';
    }

    function isAnalyzerEnabled(rootEl) {
        return rootEl.dataset.analyzer !== 'false';
    }

    function isEvalBarEnabled(rootEl) {
        return rootEl.dataset.evalbar !== 'false';
    }

    window.FelixChess.BoardController = {
        init:            initAll,
        initAll:         initAll,
        initViewer:      initViewer,
        setFen:          setFen,
        loadGame:        loadGame,
        loadTreePath:    loadTreePath,
        clearGame:       clearGame,
        reset:           reset,
        exportPgn:       exportPgn,
        getCurrentPosition: getCurrentPosition,
        seekPosition: seekPosition,
        parsePgnClocks:  parsePgnClocks
    };

    // Expose BoardMoveInput so any page (Opening Explorer, Move Tree) can
    // instantiate it without a separate script tag or bundle entry point.
    window.FelixChess.BoardMoveInput = BoardMoveInput;

})();
